import { apiClient } from '@/api/client'
import {
  BOARDS,
  boardByIdPath,
  boardMembersPath,
  boardMemberByUserPath,
  boardListsPath,
  boardListsReorderPath,
  boardListByIdPath,
  boardCardsPath,
  boardCardByIdPath,
  boardCardMovePath,
  boardCardAttachmentsPath,
  boardCardAttachmentPath,
  boardCardCommentsPath,
  boardCardChecklistsPath,
  boardCardChecklistPath,
  boardCardChecklistItemsPath,
  boardCardChecklistItemPath,
  boardCardActivitiesPath,
  boardLabelsPath,
  boardLabelByIdPath,
} from '@/api/endpoints'

// ─── Types ─────────────────────────────────────────

export interface BoardData {
  _id: string
  organizationId: string
  title: string
  description?: string
  type: string
  visibility: string
  entityType?: string
  entityId?: string
  background?: string
  createdBy: string | { _id: string; firstName: string; lastName: string; email: string; avatar?: string }
  /** Returned for the current user on the board-detail endpoint. */
  currentUserRole?: 'owner' | 'admin' | 'member' | 'viewer' | null
  isTemplate: boolean
  isArchived: boolean
  isClosed: boolean
  createdAt: string
  updatedAt: string
}

export interface BoardMemberData {
  _id: string
  boardId: string
  userId: { _id: string; firstName: string; lastName: string; email: string; avatar?: string }
  role: 'owner' | 'admin' | 'member' | 'viewer'
}

export interface BoardListData {
  _id: string
  organizationId: string
  boardId: string
  title: string
  position: number
  color?: string
  wipLimit: number
  isDoneColumn: boolean
  isArchived: boolean
}

export interface CardData {
  _id: string
  organizationId: string
  boardId: string
  listId: string
  title: string
  description: string
  position: number
  priority: string
  entityType?: string
  entityId?: string
  assignees: Array<{ _id: string; firstName: string; lastName: string; email: string; avatar?: string }>
  labels: Array<{ _id: string; name: string; color: string }>
  startDate?: string
  dueDate?: string
  completedAt?: string
  coverImage?: string
  estimate?: number
  attachments?: CardAttachmentData[]
  createdBy: string | { _id: string; firstName: string; lastName: string; email: string; avatar?: string }
  isArchived: boolean
  commentsCount?: number
  checklistTotal?: number
  checklistCompleted?: number
  createdAt: string
  updatedAt: string
}

export interface CardAttachmentData {
  _id: string
  url: string
  name: string
  mimeType: string
  size: number
  createdAt: string
}

export interface CommentData {
  _id: string
  cardId: string
  authorId: { _id: string; firstName: string; lastName: string; email: string; avatar?: string }
  content: string
  mentions: string[]
  isEdited: boolean
  createdAt: string
}

export interface ChecklistData {
  _id: string
  cardId: string
  title: string
  position: number
  items: ChecklistItemData[]
}

export interface ChecklistItemData {
  _id: string
  checklistId: string
  title: string
  isCompleted: boolean
  position: number
  /** Set on a sub-item; one level deep. */
  parentItemId?: string | null
  assigneeId?: string
  dueDate?: string
}

export interface LabelData {
  _id: string
  boardId: string
  name: string
  color: string
}

export interface ActivityData {
  _id: string
  boardId: string
  cardId: string
  actorId: { _id: string; firstName: string; lastName: string; email: string; avatar?: string }
  type: string
  details: Record<string, unknown>
  createdAt: string
}

export interface CreateBoardPayload {
  title: string
  description?: string
  type?: string
  visibility?: string
  entityType?: string
  entityId?: string
  background?: string
  templateId?: string
  memberIds?: string[]
}

export interface UpdateBoardPayload {
  title?: string
  description?: string
  visibility?: string
  background?: string
  isArchived?: boolean
  isClosed?: boolean
}

export interface CreateListPayload {
  title: string
  position?: number
  color?: string
  wipLimit?: number
  isDoneColumn?: boolean
}

export interface UpdateListPayload {
  title?: string
  color?: string
  wipLimit?: number
  isDoneColumn?: boolean
  isArchived?: boolean
}

export interface CreateCardPayload {
  title: string
  description?: string
  listId: string
  priority?: string
  entityType?: string
  entityId?: string
  assignees?: string[]
  labels?: string[]
  startDate?: string
  dueDate?: string
  coverImage?: string
  estimate?: number
  position?: number
}

export interface UpdateCardPayload {
  title?: string
  description?: string
  priority?: string
  assignees?: string[]
  labels?: string[]
  startDate?: string | null
  dueDate?: string | null
  completedAt?: string | null
  coverImage?: string
  estimate?: number
  entityType?: string
  entityId?: string
  isArchived?: boolean
}

export interface MoveCardPayload {
  listId: string
  position: number
}

// ─── API Calls ─────────────────────────────────────

function unwrap<T>(res: { data: { data?: T } | T }): T {
  const d = res.data
  if (d && typeof d === 'object' && 'data' in d) return (d as { data: T }).data
  return d as T
}

// Boards
export async function getBoards(archived = false): Promise<BoardData[]> {
  const res = await apiClient.get(BOARDS, { params: { archived: archived ? 'true' : 'false' } })
  const data = unwrap<BoardData[]>(res)
  return Array.isArray(data) ? data : []
}

export async function getBoardById(id: string): Promise<BoardData> {
  return unwrap<BoardData>(await apiClient.get(boardByIdPath(id)))
}

export async function createBoard(payload: CreateBoardPayload): Promise<BoardData> {
  return unwrap<BoardData>(await apiClient.post(BOARDS, payload))
}

export async function updateBoard(id: string, payload: UpdateBoardPayload): Promise<BoardData> {
  return unwrap<BoardData>(await apiClient.put(boardByIdPath(id), payload))
}

export async function deleteBoard(id: string): Promise<void> {
  await apiClient.delete(boardByIdPath(id))
}

export async function deleteBoardPermanently(id: string): Promise<void> {
  await apiClient.delete(`${boardByIdPath(id)}/permanent`)
}

export async function getBoardMembers(boardId: string): Promise<BoardMemberData[]> {
  const data = unwrap<BoardMemberData[]>(await apiClient.get(boardMembersPath(boardId)))
  return Array.isArray(data) ? data : []
}

export async function addBoardMember(boardId: string, userId: string, role: 'admin' | 'member' | 'viewer' = 'member'): Promise<BoardMemberData> {
  return unwrap<BoardMemberData>(await apiClient.post(boardMembersPath(boardId), { userId, role }))
}

export async function removeBoardMember(boardId: string, userId: string): Promise<void> {
  await apiClient.delete(boardMemberByUserPath(boardId, userId))
}

// Lists
export async function getBoardLists(boardId: string, archived = false): Promise<BoardListData[]> {
  const data = unwrap<BoardListData[]>(await apiClient.get(boardListsPath(boardId), {
    params: archived ? { archived: 'true' } : undefined,
  }))
  return Array.isArray(data) ? data : []
}

export async function createBoardList(boardId: string, payload: CreateListPayload): Promise<BoardListData> {
  return unwrap<BoardListData>(await apiClient.post(boardListsPath(boardId), payload))
}

export async function updateBoardList(boardId: string, listId: string, payload: UpdateListPayload): Promise<BoardListData> {
  return unwrap<BoardListData>(await apiClient.put(boardListByIdPath(boardId, listId), payload))
}

export async function reorderBoardLists(boardId: string, listIds: string[]): Promise<void> {
  await apiClient.put(boardListsReorderPath(boardId), { listIds })
}

export async function deleteBoardList(boardId: string, listId: string): Promise<void> {
  await apiClient.delete(boardListByIdPath(boardId, listId))
}

// Cards
export async function getBoardCards(boardId: string, archived = false): Promise<CardData[]> {
  const data = unwrap<CardData[]>(await apiClient.get(boardCardsPath(boardId), {
    params: archived ? { archived: 'true' } : undefined,
  }))
  return Array.isArray(data) ? data : []
}

export async function getCardById(boardId: string, cardId: string): Promise<CardData> {
  return unwrap<CardData>(await apiClient.get(boardCardByIdPath(boardId, cardId)))
}

export async function createCard(boardId: string, payload: CreateCardPayload): Promise<CardData> {
  return unwrap<CardData>(await apiClient.post(boardCardsPath(boardId), payload))
}

export async function updateCard(boardId: string, cardId: string, payload: UpdateCardPayload): Promise<CardData> {
  return unwrap<CardData>(await apiClient.put(boardCardByIdPath(boardId, cardId), payload))
}

export async function moveCard(boardId: string, cardId: string, payload: MoveCardPayload): Promise<CardData> {
  return unwrap<CardData>(await apiClient.put(boardCardMovePath(boardId, cardId), payload))
}

export async function deleteCard(boardId: string, cardId: string): Promise<void> {
  await apiClient.delete(boardCardByIdPath(boardId, cardId))
}

export async function uploadCardAttachments(boardId: string, cardId: string, files: File[]): Promise<CardData> {
  const formData = new FormData()
  files.forEach((file) => formData.append('files', file))
  return unwrap<CardData>(await apiClient.post(boardCardAttachmentsPath(boardId, cardId), formData))
}

export async function deleteCardAttachment(boardId: string, cardId: string, attachmentId: string): Promise<void> {
  await apiClient.delete(boardCardAttachmentPath(boardId, cardId, attachmentId))
}

// Comments
export async function getCardComments(boardId: string, cardId: string): Promise<CommentData[]> {
  const data = unwrap<CommentData[]>(await apiClient.get(boardCardCommentsPath(boardId, cardId)))
  return Array.isArray(data) ? data : []
}

export async function addCardComment(boardId: string, cardId: string, payload: { content: string; mentions?: string[] }): Promise<CommentData> {
  return unwrap<CommentData>(await apiClient.post(boardCardCommentsPath(boardId, cardId), payload))
}

// Checklists
export async function getCardChecklists(boardId: string, cardId: string): Promise<ChecklistData[]> {
  const data = unwrap<ChecklistData[]>(await apiClient.get(boardCardChecklistsPath(boardId, cardId)))
  return Array.isArray(data) ? data : []
}

export async function createChecklist(boardId: string, cardId: string, payload: { title: string }): Promise<ChecklistData> {
  return unwrap<ChecklistData>(await apiClient.post(boardCardChecklistsPath(boardId, cardId), payload))
}

export async function deleteChecklist(boardId: string, cardId: string, checklistId: string): Promise<void> {
  await apiClient.delete(boardCardChecklistPath(boardId, cardId, checklistId))
}

export async function createChecklistItem(boardId: string, cardId: string, checklistId: string, payload: { title: string; parentItemId?: string }): Promise<ChecklistItemData> {
  return unwrap<ChecklistItemData>(await apiClient.post(boardCardChecklistItemsPath(boardId, cardId, checklistId), payload))
}

export async function updateChecklistItem(boardId: string, cardId: string, checklistId: string, itemId: string, payload: { isCompleted?: boolean; title?: string }): Promise<ChecklistItemData> {
  return unwrap<ChecklistItemData>(await apiClient.put(boardCardChecklistItemPath(boardId, cardId, checklistId, itemId), payload))
}

export async function deleteChecklistItem(boardId: string, cardId: string, checklistId: string, itemId: string): Promise<void> {
  await apiClient.delete(boardCardChecklistItemPath(boardId, cardId, checklistId, itemId))
}

// Activities
export async function getCardActivities(boardId: string, cardId: string): Promise<ActivityData[]> {
  const data = unwrap<ActivityData[]>(await apiClient.get(boardCardActivitiesPath(boardId, cardId)))
  return Array.isArray(data) ? data : []
}

// Labels
export async function getBoardLabels(boardId: string): Promise<LabelData[]> {
  const data = unwrap<LabelData[]>(await apiClient.get(boardLabelsPath(boardId)))
  return Array.isArray(data) ? data : []
}

export async function createBoardLabel(boardId: string, payload: { name: string; color?: string }): Promise<LabelData> {
  return unwrap<LabelData>(await apiClient.post(boardLabelsPath(boardId), payload))
}

export async function updateBoardLabel(
  boardId: string,
  labelId: string,
  payload: { name: string; color?: string },
): Promise<LabelData> {
  return unwrap<LabelData>(await apiClient.put(boardLabelByIdPath(boardId, labelId), payload))
}

export async function deleteBoardLabel(boardId: string, labelId: string): Promise<void> {
  await apiClient.delete(boardLabelByIdPath(boardId, labelId))
}
